import { isEqual } from 'lodash';
import { useCallback, useEffect } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { type AsOfDateSetting, CURRENT_AS_OF_DATE, usePrevious } from 'venn-utils';
import { blockLimitedRequestSubjects } from '../studio/configuration/blockConfig';
import {
  blockAsOfDateSetting,
  type BlockAsOfDateSettingCount,
  type BlockAsOfDateSettingIndices,
} from '../studio/configuration/historicalAssets';

export const useBlockAsOfDateSetting = (blockId: string) => {
  const [blockAsOfDate, setBlockAsOfDate] = useRecoilState(blockAsOfDateSetting(blockId));

  const setAsOfDateByIndex = useCallback(
    (index: BlockAsOfDateSettingIndices, asOfDate: AsOfDateSetting) => {
      setBlockAsOfDate((prevValue) => {
        const newValue: typeof prevValue = [...prevValue];
        newValue[index] = asOfDate;
        return newValue;
      });
    },
    [setBlockAsOfDate],
  );

  return {
    blockAsOfDate,
    setAsOfDateByIndex,
  };
};

export const useResetAsOfDateOnSubjectChange = ({
  blockId,
  numSubjects,
}: {
  blockId: string;
  numSubjects: BlockAsOfDateSettingCount | BlockAsOfDateSettingIndices;
}) => {
  const { setAsOfDateByIndex } = useBlockAsOfDateSetting(blockId);
  const subjects = useRecoilValue(blockLimitedRequestSubjects(blockId));
  const previousSubjects = usePrevious(subjects, isEqual);

  useEffect(() => {
    if (previousSubjects === null) {
      return;
    }
    for (let index = 0; index < numSubjects; index++) {
      if (subjects[index] !== previousSubjects?.[index]) {
        setAsOfDateByIndex(index as BlockAsOfDateSettingIndices, CURRENT_AS_OF_DATE);
      }
    }
  }, [numSubjects, previousSubjects, setAsOfDateByIndex, subjects]);
};
